import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./CareerPage.scss";
import { InputText } from "primereact/inputtext";
import { AiOutlineCheckCircle } from "react-icons/ai";
import Nav from "../../Home/navbar/nav";
import { Helmet } from "react-helmet";
import blackBackground from "../../Assets/Career/blackimage.webp";

import holographicTextureBackgroundTwo from "../../Assets/Career/lavender-image.webp";
import jetImage from "../../Assets/Career/jetImage.webp";
import growWithAlthiBackground from "../../Assets/Career/growWithAlthi-background.webp";
import growCard from "../../Assets/Career/grow-cards.webp";

gsap.registerPlugin(ScrollTrigger);

const growCards = [
  {
    title: "Learning & Career Growth",
    desc: "Build your skills through continuous learning, mentorship, and exciting real-world projects.",
  },
  {
    title: "Performance Recognition",
    desc: "We appreciate dedication and reward employees for their contributions and achievements.",
  },
  {
    title: "Innovation & Creativity",
    desc: "Bring your ideas to life in an environment that encourages creativity and continuous improvement.",
  },
  {
    title: "Flexible Work Environment",
    desc: "We promote a healthy work-life balance with flexibility and a supportive workplace culture.",
  },
];

const CareerPage = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    location: "",
    position: "",
    experience: "",
    base64Data: "",
    resumeFileName: "",
    resumeContentType: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pdfData, setPdfData] = useState("");
  const [errorData, setErrorData] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showAllOpenings, setShowAllOpenings] = useState(false);
  const fileUploadRef = useRef(null);
  const formSectionRef = useRef(null);
  const [expandedJobIndex, setExpandedJobIndex] = useState(null);


useEffect(() => {
  const ctx = gsap.context(() => {

    gsap.set(
      ".career-page-text h1, .career-page-text p",
      {
        opacity: 1,
        y: 0,
      }
    );

    gsap.utils.toArray(".career-gsap-animate").forEach((element) => {
      gsap.fromTo(
        element,
        {
          opacity: 0,
          y: 40,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: element,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        }
      );
    });

  });

  return () => {
    ctx.revert();
  };
}, []);


const onSelect = (e) => {
  const file = e.files[0];

  if (!file) return;

  const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ];

  if (!allowedTypes.includes(file.type)) {
    setErrorData("Please upload only PDF, DOC, or DOCX files.");
    return;
  }

  if (file.size > 10 * 1024 * 1024) {
    setErrorData("File size should not exceed 10 MB.");
    return;
  }

  setErrorData("");

  const reader = new FileReader();

  reader.onloadend = () => {
    const base64 = reader.result.split(",")[1];

    setPdfData(base64);

    setFormData((prev) => ({
      ...prev,
      base64Data: base64,
      resumeFileName: file.name,
      resumeContentType: file.type,
    }));
  };

  reader.readAsDataURL(file);
};

const jobOpenings = [
  {
    title: "IT Sales Executive",
    experience: "1-2 Years (IT Sales Experience)",
    location: "Remote / Azhagappapuram, Kanyakumari",

    description:
      "We are seeking a dynamic and enthusiastic IT Sales Executive with 1-2 years of experience in IT sales and business development. The ideal candidate should possess strong communication, lead generation, and client relationship management skills with the ability to promote IT services and achieve sales targets. The candidate will be responsible for identifying new business opportunities, understanding client requirements, coordinating with technical teams, managing client communications, and converting leads into long-term business relationships while contributing to team collaboration and overall business growth.",
    responsibilities: [
      "Generate new business opportunities through cold calling, email campaigns, networking, and lead generation activities.",
      "Identify prospective clients and maintain strong business relationships.",
      "Understand customer requirements and propose suitable IT solutions and services.",
      "Promote company services including Web Development, Mobile Application Development, Software Development, Digital Marketing, AI & Data Science Solutions, and UI/UX Services.",
      "Conduct client meetings, presentations, and product/service demonstrations.",
      "Prepare business proposals, quotations, and sales documentation.",
      "Coordinate with technical and project teams for requirement gathering and project discussions.",
      "Maintain accurate records of leads, opportunities, and sales activities in CRM tools.",
      "Achieve monthly, quarterly, and annual sales targets.",
      "Follow up regularly with clients to ensure customer satisfaction and repeat business.",
      "Support team coordination and contribute to achieving overall business objectives."
    ],

    technicalSkills: [
      "Knowledge of IT services and software solutions.",
      "Understanding of B2B sales process.",
      "Basic understanding of Software Development Lifecycle (SDLC).",
      "Experience in lead generation and client acquisition."
    ],

    softSkills: [
      "Excellent verbal and written communication skills.",
      "Strong negotiation and presentation abilities.",
      "Good interpersonal and relationship-building skills.",
      "Target-oriented and self-motivated approach.",
      "Strong problem-solving and analytical thinking abilities.",
      "Ability to work independently and within a team environment."
    ],

    eligibility: [
      "Bachelor’s Degree in Business Administration, Marketing, IT, or related field.",
      "1–2 years of experience in IT Sales, Software Sales, or Business Development.",
      "Experience in domestic or international IT sales is preferred.",
      "Immediate joining availability or notice period up to 30 days is preferred."
    ],

    benefits: [
      "Competitive Salary Package.",
      "Attractive Incentives and Performance Bonuses.",
      "Career Growth Opportunities.",
      "Professional Work Environment.",
      "Learning and Development Opportunities."
    ]
  }
];

const visibleOpenings = showAllOpenings
  ? jobOpenings
  : jobOpenings.slice(0, 4);

  const toggleJobAccordion = (index) => {
    setExpandedJobIndex(expandedJobIndex === index ? null : index);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (["firstName", "lastName", "location", "position"].includes(name)) {
      updatedValue = value.replace(/[^A-Za-z\s]/g, "");
    }

    if (name === "experience") {
      updatedValue = value.replace(/\D/g, "").slice(0, 2);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));
  };

  const handleApplyNowClick = (jobTitle) => {
    setFormData((prev) => ({
      ...prev,
      position: jobTitle,
    }));
    if (!formSectionRef.current) return;

    const targetY =
      formSectionRef.current.getBoundingClientRect().top + window.scrollY;

    const scrollProxy = { y: window.scrollY };

    gsap.to(scrollProxy, {
      y: targetY,
      duration: 1,
      ease: "power2.inOut",
      onUpdate: () => window.scrollTo(0, scrollProxy.y),
    });
  };

const handleSubmit = async (event) => {
  event.preventDefault();

  if (isSubmitting) return;
  if (!pdfData) {
    setErrorData("Please Upload Your Resume.");
    return;
  }

  setIsSubmitting(true);
  setErrorData("");

  const payload = {
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    phone: `+91 ${formData.phoneNumber}`,
    applyingFor: formData.position,
    currentLocation: formData.location,
    totalExperience: formData.experience,
    resume: {
      filename: formData.resumeFileName,
      data: pdfData,
      contentType: formData.resumeContentType,
    },
  };

  try {
  await fetch(
      "https://b5alx6fvg8.execute-api.us-east-1.amazonaws.com/ses/careerSubmit",
      {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }

    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      location: "",
      position: "",
      experience: "",
      base64Data: "",
      resumeFileName: "",
      resumeContentType: "",
    });

    setPdfData("");
    setShowSuccessMessage(true);

    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);

  } catch (error) {
    console.error("Error submitting form:", error);
    setErrorData("Something went wrong. Please try again.");
  } finally {
    setIsSubmitting(false);
  }
};


  return (
    <>
      <Helmet>
        <title>Careers | Althi Solutions</title>
        <link
          rel="preload"
          as="image"
          href={blackBackground}
          type="image/webp"
        />

        <link
          rel="preload"
          as="image"
          href={holographicTextureBackgroundTwo}
          type="image/webp"
        />
        <meta name="description" content="Explore exciting career opportunities to propel your skills and expertise." />
        <link rel="canonical" href="https://www.althisolutions.com/careers" />
        <meta name="robots" content="index, follow" />
        <meta
            property="og:type"
            content="website"
            />

            <meta
            property="og:title"
            content="Careers | Althi Solutions"
            />

            <meta
            property="og:description"
            content="Explore exciting career opportunities to propel your skills and expertise."
            />

            <meta
            property="og:url"
            content="https://www.althisolutions.com/careers"
            />

            <meta
            property="og:image"
            content="https://www.althisolutions.com/thumbnail.jpg"
            />

            <meta
            property="og:site_name"
            content="Althi Solutions"
            />

            <meta
            name="twitter:card"
            content="summary_large_image"
            />

            <meta
            name="twitter:site"
            content="@AlthiSolutions"
            />

            <meta
            name="twitter:creator"
            content="@AlthiSolutions"
            />

            <meta
            name="twitter:title"
            content="Careers | Althi Solutions"
            />

            <meta
            name="twitter:description"
            content="Explore exciting career opportunities to propel your skills and expertise."
            />

            <meta
            name="twitter:image"
            content="https://www.althisolutions.com/thumbnail.jpg"
            />

            </Helmet>

      <Nav />

    <div className="career-page-wrapper">
      <section className="career-page">
      <img src={blackBackground} alt="decorative background" className="career-bg-main"/>
      <img src={holographicTextureBackgroundTwo} alt="decorative texture" className="career-bg-left" fetchPriority="high"/>
      <img src={jetImage} alt="Rocket symbolizing career growth" className="career-jet"/>

      <div className="career-content-container">
        <div className="career-page-text">
          <h1>
            Join Our Growing<br />
            Team
          </h1>

          <br />

          <p>
            Join Althi Solutions to learn, innovate, and grow your
            <br />
            career with a passionate and forward-thinking team.
          </p>
        </div>
      </div>
    </section>

    <section className="current-openings-section">
  <div className="current-openings-container">
    <h2 className="career-gsap-animate">Current Openings</h2>
    <p className="opening-subtitle career-gsap-animate">
      Beyond jobs, we open the doors for your growth.
    </p>

    {visibleOpenings.map((job, index) => {
      const isCurrentJobExpanded = expandedJobIndex === index;

      return (
        <div
          key={index}
          className={`opening-card-wrapper ${
            isCurrentJobExpanded ? "expanded" : ""
          }`}
          style={{ marginBottom: "16px" }}
        >
          <div
            className="opening-card"
            onClick={() => toggleJobAccordion(index)}
          >
            <h3>{job.title}</h3>

            <svg
              className="arrow-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              style={{
                transform: isCurrentJobExpanded
                  ? "rotate(90deg)"
                  : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            >
              <path
                d="M9 5l7 7-7 7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div
            className={`job-details-panel ${
              isCurrentJobExpanded ? "show" : ""
            }`}
          >
            <div className="job-details-content">

              <div className="job-info-row">
                <div className="job-info-card">
                  <span className="job-info-label">Experience</span>
                  <p>{job.experience}</p>
                </div>

                <div className="job-info-card">
                  <span className="job-info-label">Location</span>
                  <p>{job.location}</p>
                </div>
              </div>

              <div className="job-description-block">
                <h4>Job Description</h4>
                <p>{job.description}</p>
              </div>

              <div className="job-full-width-panel">
                <div className="panel-header">
                  <span className="panel-number">01</span>
                  <h4 style={{fontSize:"18px"}}>Key Responsibilities</h4>
                </div>

                <ul className="responsiblity-items">
                  {job.responsibilities.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="skills-flex-container">
                <div className="job-panel">
                  <div className="panel-header">
                    <span className="panel-number">02</span>
                    <h4>Technical Skills</h4>
                  </div>

                  <ul>
                    {job.technicalSkills.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="job-panel">
                  <div className="panel-header">
                    <span className="panel-number">03</span>
                    <h4>Soft Skills</h4>
                  </div>

                  <ul>
                    {job.softSkills.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="skills-flex-container additional-meta">
                <div className="job-panel minimalist-panel">
                  <h4>Eligibility Criteria</h4>

                  <ul>
                    {job.eligibility.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="job-panel minimalist-panel">
                  <h4>Benefits & Perks</h4>

                  <ul>
                    {job.benefits.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="panel-action-bar">
                <button
                  type="button"
                  className="accordion-apply-now-btn"
                  onClick={() => handleApplyNowClick(job.title)}
                >
                  Apply Now <span className="btn-arrow">→</span>
                </button>
              </div>

            </div>
          </div>
        </div>
      );
    })}

        {jobOpenings.length > 4 && !showAllOpenings ? (
          <button
            className="see-all-openings-btn"
            onClick={() => setShowAllOpenings(true)}
            style={{
              display: "block",
              margin: "50px auto 0 auto",
              textAlign: "center",
            }}
          >
            See all openings <span className="arrow">→</span>
          </button>
        ) : jobOpenings.length > 0 ? (
          <div className="future-opportunities-message">
            <h3 className="career-gsap-animate">Didn't find the right role?</h3>
            <p className="career-gsap-animate">
              We're always looking for talented individuals to join our team.
              Submit your profile using the application form below and we'll
              reach out when a suitable opportunity becomes available.
            </p>
          </div>
        ) : (
          <div className="future-opportunities-message no-openings">
            <h3 className="career-gsap-animate">No current openings at the moment</h3>
            <p className="career-gsap-animate">
              We're always interested in connecting with talented professionals.
              Share your profile using the application form below and we'll
              contact you when a suitable opportunity becomes available.
            </p>
          </div>
        )}
      </div>
    </section>

      <section className="grow-with-althi-section" style={{ backgroundImage: `url(${growWithAlthiBackground})` }}>
        <div className="grow-with-althi-container">
          <div className="grow-top-section">
            <div className="grow-heading">
              <h2 className="career-gsap-animate">Grow With Althi</h2>
            </div>
            <div className="grow-description">
              <p className="career-gsap-animate">At Althi Solutions, we value your ideas, fuel your growth, and celebrate your success.</p>
            </div>
          </div>

          <div className="grow-cards">
            {growCards.map((card, idx) => (
              <div
                key={idx}
                className="grow-card"
                style={{
                  backgroundImage: `url(${growCard})`,
                }}
              >
                <h3>{card.title}</h3>
                <p>{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="join-team-section" ref={formSectionRef}>
        <div className="join-team-container">
          <div className="join-team-header">
            <h2 className="join-team-title career-gsap-animate">Join Our Team !</h2>
            <p className="join-team-subtitle career-gsap-animate">We help organizations across the globe achieve digital transformation.</p>
          </div>

          <form className="join-team-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label>First Name<span className="required">*</span></label>
              <InputText name="firstName" value={formData.firstName} onChange={handleChange} placeholder="Enter first name" required />
            </div>

            <div className="form-group">
              <label>Last Name</label>
              <InputText name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Enter last name" />
            </div>

            <div className="form-group">
              <label>Email<span className="required">*</span></label>
              <InputText type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Enter email" required />
            </div>

          <div className="form-group">
              <label>
                Phone Number<span className="required">*</span>
              </label>
            <InputText
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={(e) => {
                let value = e.target.value.replace(/\D/g, "");

                if (value.length > 5) {
                  value = value.slice(0, 5) + "-" + value.slice(5, 10);
                }

                setFormData({
                  ...formData,
                  phoneNumber: value,
                });
              }}
              placeholder="xxxxx-xxxxx"
              maxLength={11}
              required
            />
            </div>

            <div className="form-group">
              <label>Current Location<span className="required">*</span></label>
              <InputText name="location" value={formData.location} onChange={handleChange} placeholder="Enter current location" required/>
            </div>

            <div className="form-group">
              <label>Applying For<span className="required">*</span></label>
              <InputText name="position" value={formData.position} onChange={handleChange} placeholder="Enter your role" required />
            </div>

            <div className="form-group">
              <label>Total Experience ( In Years )</label>
              <InputText name="experience" value={formData.experience} onChange={handleChange} placeholder="Enter your years of experience" />
            </div>

            <div className="form-group">
              <label>Upload Resume<span className="required">*</span></label>
              <input 
                type="file" 
                id="resume-upload" 
                accept=".pdf,.doc,.docx"
                style={{ display: "none" }} 
                onChange={(e) => onSelect({ files: e.target.files })}
              />
              <div 
                className="upload-resume-container" 
                onClick={() => document.getElementById("resume-upload").click()}
                style={{ cursor: "pointer" }}
              >
                <div className="upload-icon-box">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v4M17 8l-5-5-5 5M12 3v12"/>
                  </svg>
                </div>
                <div className="upload-text">
                  <p className="upload-main">
                    {formData.resumeFileName || "Upload your CV here"}
                  </p>
                  <p className="upload-sub">Max. 10 MB ( Type: PDF, DOC, DOCX )</p>
                </div>
              </div>
            </div>

            {(errorData || showSuccessMessage) && (
          <div className="form-message-container">
            {errorData && (
              <p className="error-msg">{errorData}</p>
            )}

            {showSuccessMessage && (
              <div className="success-messages">
                <div className="success-message-contents">
                  <div className="success-message-headings">
                    Submitted Successfully !
                  </div>
                  <AiOutlineCheckCircle className="check-icon" />
                </div>
              </div>
            )}
          </div>
          )}

      <div className="submit-btn-container">
        <button
          type="submit"
          className="form-submit-btn"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
          <span className="submit-arrow">→</span>
        </button>
      </div>
          </form>
        </div>
      </section>
      </div>
    </>
  );
};

export default CareerPage;